
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import PaginationConfig from "@/entities/PaginationConfig";
import paginationConfig from "@/data/paginationConfig";
import UiButton from "@/components/button/ui-button.vue";
import DefaultRadioButton from "@/components/common/DefaultRadioButton.vue";
import ENABLE_RESOURCES_ACCESS from "@/apollo/company/EnableResourcesAccess.gql";
import DISABLE_RESOURCES_ACCESS from "@/apollo/company/DisableAllResourceAccessType.gql";
import SELECTED_RESOURCES_ACCESS from "@/apollo/company/SelectedResourceAccessType.gql";
import {
  Company,
  Manager,
  Manager_Bool_Exp,
  User_Profile,
} from "@/generated/graphql";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import MarketplaceAccessType from "@/data/companySettings/MarketplaceAccessType";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import LIST_MANAGERS from "@/apollo/managers/ListSingleManagers.gql";
import GET_RESOURCE_ACCESS_DATA from "@/apollo/company/GetResourceAccessData.gql";
import FormGroup from "@/components/form/FormGroup.vue";
import UPDATE_MARKETPLACE_ACCESS_TYPE from "@/apollo/company/UpdateResourceAccessType.gql";
import ValidatableEmployeesMultiselect from "@/components/autocompleteMultiselects/ValidatableEmployeesMultiselect.vue";
import ManagersMultiselect from "@/components/autocompleteMultiselects/ManagersMultiselectNew.vue";
import { getLocationOrPlaceholder } from "@/helpers/locations/getLocationInfo";

interface MarketplaceAccessRequestsQueryConditions {
  limit: number;
  offset: number;
  conditions: Manager_Bool_Exp;
}

@Component({
  components: {
    ValidatableEmployeesMultiselect,
    ManagersMultiselect,
    DefaultRadioButton,
    EmptyListPlaceholder,
    FormGroup,
    UiButton,
    LoadingOverlay,
  },
  apollo: {
    companySettings: {
      query: GET_RESOURCE_ACCESS_DATA,
      variables() {
        return {
          id: this.company.id,
        };
      },
      update(data) {
        const marketplaceAccess =
          data.company_by_pk.allow_managers_view_all_contractors;
        const selectedManagerIds = data.company_by_pk.managers.map(
          (manager: Manager) => manager.id
        );

        this.chosenMarketplaceAccessType = marketplaceAccess;
        this.initialMarketplaceAccessType = marketplaceAccess;
        this.selectedManagersIds =
          marketplaceAccess === MarketplaceAccessType.SELECTED
            ? selectedManagerIds
            : [];
        this.initialSelectedManagerIds = selectedManagerIds;
      },
    },
  },
})
export default class MarketplaceAccessRequests extends Vue {
  @Prop({ type: Object, required: true }) readonly company!: Company;

  processing: boolean = false;
  MarketplaceAccessType = MarketplaceAccessType;
  chosenMarketplaceAccessType: MarketplaceAccessType =
    MarketplaceAccessType.ALL;
  initialMarketplaceAccessType: MarketplaceAccessType =
    MarketplaceAccessType.ALL;
  selectedManagersIds: string[] = [];
  initialSelectedManagerIds: string[] = [];
  managerMultiselectError: boolean = false;

  managers: Manager[] = [];
  paginationConfig: PaginationConfig = {
    ...paginationConfig,
  };
  get managerArgs() {
    const filterConditions = [
      {
        resources_access_allowed: { _eq: true },
      },
    ];
    return {
      where: {
        _and: filterConditions,
      },
    };
  }

  get radioButtonGroupName(): string {
    return "company_access_type";
  }

  get isLoading(): boolean {
    return this.$apollo.queries.companySettings.loading;
  }

  get radioButtonClasses(): string {
    return "flex items-center py-1.5 pl-2 space-x-1.5 text-sm";
  }

  getLocation(profile: User_Profile): string {
    const { city, country } = profile;
    return getLocationOrPlaceholder([city, country]);
  }
  @Watch("selectedManagersIds")
  onSelectedIdsChange(selectedManagersIds: string[]): void {
    if (this.managerMultiselectError) {
      this.managerMultiselectError = selectedManagersIds.length === 0;
    }
  }

  @Watch("chosenMarketplaceAccessType")
  onChosenMarketplaceAccessTypeChange() {
    this.managerMultiselectError = false;
  }

  isChecked(value: string): boolean {
    return this.chosenMarketplaceAccessType === value;
  }
  get managerMultiselectDisabled(): boolean {
    return this.chosenMarketplaceAccessType !== MarketplaceAccessType.SELECTED;
  }

  async update(): Promise<void> {
    const managerSelected = this.selectedManagersIds.length > 0;
    if (!managerSelected && !this.managerMultiselectDisabled) {
      this.managerMultiselectError = true;
      return;
    }

    this.processing = true;

    try {
      await this.updateMarketplaceAccess();

      window.location.reload();
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Updated successfully")
      );
    } catch (err) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage("Something went wrong")
      );
    } finally {
      this.processing = false;
    }
  }

  private updateMarketplaceAccess() {
    this.$apollo.mutate({
      mutation: UPDATE_MARKETPLACE_ACCESS_TYPE,
      variables: {
        companyId: this.company.id,
        resourceAccessType: this.chosenMarketplaceAccessType,
      },
    });
    if (this.chosenMarketplaceAccessType === MarketplaceAccessType.DISABLED) {
      return this.$apollo.mutate({
        mutation: DISABLE_RESOURCES_ACCESS,
        variables: {
          companyId: this.company.id,
        },
      });
    } else if (this.chosenMarketplaceAccessType === MarketplaceAccessType.ALL) {
      return this.$apollo.mutate({
        mutation: ENABLE_RESOURCES_ACCESS,
        variables: {
          companyId: this.company.id,
        },
      });
    } else {
      const deletedManagerIds = this.initialSelectedManagerIds;

      const addedManagerIds = this.selectedManagersIds;
      return this.$apollo.mutate({
        mutation: SELECTED_RESOURCES_ACCESS,
        variables: {
          deletedManagerIds,
          addedManagerIds,
        },
      });
    }
  }
}
